import { T } from '@/ui/Text/Text';
import React, { useMemo, useCallback, useEffect } from 'react';
import { Flex } from '@/ui/Flex/Flex';

import { useIntl } from 'react-intl';
import { IconChannelSelective, IconNFCReaderPrimary } from '@/icons';
import CustomSwitch from '@/ui/Switch/Switch';
import HorizontalDivider from '@/ui/HorizontalDivider';
import CustomSelect from '@/ui/Select/Select';
import { SensorNFCAllNodeType } from '../../Nodes/types';
import FAQSection from '../FAQSection';
import { StyledSettings, StyledSettingsContent } from '../styles';
import { GeneralSettingsType } from '../types';

function NFCSettingsAll({
  settings,
  selectedNode,
  updateNodeData,
  disabled,
}: {
  settings: GeneralSettingsType;
  selectedNode: SensorNFCAllNodeType;
  updateNodeData: (id: string, newData: any) => void;
  disabled?: boolean;
}) {
  const intl = useIntl();

  const relevantData = selectedNode.data.options;

  const channelOptions = useMemo(() => {
    return settings.nfcReaders.map((_, index) => ({
      label: index + 1,
      value: index + 1,
    }));
  }, [settings.nfcReaders]);

  const getAvailableOptions = useCallback(
    (exclude: number | null) => {
      return channelOptions.filter((option) => option.value !== exclude);
    },
    [channelOptions],
  );

  // Helper function to update channel data
  const updateChannelData = useCallback(
    (
      displayActionType: 'splitScreenChannels' | 'combiScreenChannels',
      channel: 'channelOne' | 'channelTwo',
      value: any,
    ) => {
      updateNodeData(selectedNode.id, {
        ...selectedNode.data,
        options: {
          ...relevantData,
          [displayActionType]: {
            ...relevantData[displayActionType],
            [channel]: value,
          },
        },
      });
    },
    [selectedNode.id, selectedNode.data, relevantData, updateNodeData],
  );

  const faqs = [
    {
      question: intl.formatMessage({
        defaultMessage:
          'What is important to know when activating Channel Selective ?',
        id: 'Y4yUt3',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'When Channel Selective is activated in the logic editor, you need to make sure that Channel Selective is also activated in the CMS. Otherwise, the system will not work as expected.',
        id: '0jZ8Lj',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'What are the Splitscreen and Combination Channels?',
        id: 'anUXXV',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Splitscreen and Combination Channels define on which channels Splitscreen or Combination is activated, e.g. if you select Channel 1 and Channel 2, the Splitscreen or Combination will be activated when two Trihows are detected on either Channel 1 or Channel 2. If you select "Any" and "Any" as Channels, the Splitscreen or Combination will be activated when two Trihows are detected on any channel.',
        id: 'aEPwVa',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'How to define a Combination in the CMS?',
        id: 'IKxPaF',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'To define a Combination in the CMS, you need to map the combination slide to a Generic Sensor and type type the generic key using the following format: "TriggerIdTrihowOne_TriggerIdTrihowTwo". If Language Selective is activated, you need to add the language code at the end, e.g. "TriggerIdTrihowOne_TriggerIdTrihowTwo|EN".',
        id: 'd+iBUb',
      }),
    },
  ];

  // Render channel selects
  const renderChannelSelects = (
    type: 'splitScreenChannels' | 'combiScreenChannels',
    title: string,
  ) => {
    const channels = relevantData[type];

    return (
      <Flex direction="column" gap={4}>
        <T body1>{title}</T>
        <Flex gap={4}>
          <CustomSelect
            key="channelOne"
            placeholder="Channel"
            icon={<IconChannelSelective />}
            disabled={disabled ?? false}
            isSearchable={false}
            options={[
              ...getAvailableOptions(Number(channels.channelTwo)),
              {
                label: intl.formatMessage({
                  defaultMessage: 'Any',
                  id: 'ToO3/h',
                }),
                value: null,
              },
            ]}
            size="small"
            onChange={(e) => updateChannelData(type, 'channelOne', e?.value)}
            value={
              channels.channelOne
                ? {
                    label: channels.channelOne,
                    value: channels.channelOne,
                  }
                : {
                    label: intl.formatMessage({
                      defaultMessage: 'Any',
                      id: 'ToO3/h',
                    }),
                    value: null,
                  }
            }
          />
          <CustomSelect
            key="channelTwo"
            placeholder="Channel"
            icon={<IconChannelSelective />}
            disabled={disabled ?? false}
            isSearchable={false}
            options={[
              ...getAvailableOptions(Number(channels.channelOne)),
              {
                label: intl.formatMessage({
                  defaultMessage: 'Any',
                  id: 'ToO3/h',
                }),
                value: null,
              },
            ]}
            size="small"
            onChange={(e) => {
              updateChannelData(type, 'channelTwo', e?.value);
            }}
            value={
              channels.channelTwo
                ? {
                    label: channels.channelTwo,
                    value: channels.channelTwo,
                  }
                : {
                    label: intl.formatMessage({
                      defaultMessage: 'Any',
                      id: 'ToO3/h',
                    }),
                    value: null,
                  }
            }
          />
        </Flex>
      </Flex>
    );
  };

  useEffect(() => {
    // When selecting "any" for both channels in splitscreen or combination, disable the other option
    if (
      relevantData.splitScreenChannels.channelOne === null &&
      relevantData.splitScreenChannels.channelTwo === null &&
      relevantData.combiScreen &&
      relevantData.splitScreen
    ) {
      updateNodeData(selectedNode.id, {
        ...selectedNode.data,
        options: {
          ...relevantData,
          combiScreen: false,
        },
      });
    }
    if (
      relevantData.combiScreenChannels.channelOne === null &&
      relevantData.combiScreenChannels.channelTwo === null &&
      relevantData.combiScreen &&
      relevantData.splitScreen
    ) {
      updateNodeData(selectedNode.id, {
        ...selectedNode.data,
        options: {
          ...relevantData,
          splitScreen: false,
        },
      });
    }
  }, [relevantData.splitScreenChannels, relevantData.combiScreenChannels]);
  return (
    <StyledSettings data-testid="sensor-nfcAll-settings">
      <Flex align="center" gap={8}>
        <IconNFCReaderPrimary />
        <Flex direction="column">
          <T h3>
            {intl.formatMessage({
              defaultMessage: 'All NFC Readers',
              id: 'TGcYbX',
            })}
          </T>
        </Flex>
      </Flex>
      <StyledSettingsContent>
        <Flex direction="column" gap={8}>
          <Flex align="center" justify="space-between">
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Channel Selective',
                id: 'AkcHaO',
              })}
            </T>
            <CustomSwitch
              checked={relevantData.channelSelective}
              disabled={disabled ?? false}
              onChange={() =>
                updateNodeData(selectedNode.id, {
                  ...selectedNode.data,
                  options: {
                    ...relevantData,
                    channelSelective: !relevantData.channelSelective,
                  },
                })
              }
            />
          </Flex>
        </Flex>
        <HorizontalDivider />
        <Flex direction="column" gap={8}>
          <Flex align="center" justify="space-between">
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Splitscreen',
                id: 'cKS6zQ',
              })}
            </T>
            <CustomSwitch
              checked={relevantData.splitScreen}
              disabled={
                (disabled ||
                  (relevantData.combiScreen &&
                    relevantData.combiScreenChannels.channelOne === null &&
                    relevantData.combiScreenChannels.channelTwo === null)) ??
                false
              }
              onChange={() => {
                const splitScreen = !relevantData.splitScreen;

                // Determine if we need to disable combiScreen due to any any
                const shouldDisableCombi =
                  relevantData.combiScreen &&
                  relevantData.splitScreenChannels.channelOne === null &&
                  relevantData.splitScreenChannels.channelTwo === null;

                updateNodeData(selectedNode.id, {
                  ...selectedNode.data,
                  options: {
                    ...relevantData,
                    splitScreen,
                    // turn off combiScreen if necessary
                    combiScreen: shouldDisableCombi
                      ? false
                      : relevantData.combiScreen,
                  },
                });
              }}
            />
          </Flex>
          {relevantData.splitScreen && (
            <Flex direction="column" gap={8}>
              {renderChannelSelects(
                'splitScreenChannels',
                'Splitscreen Channels',
              )}
            </Flex>
          )}
        </Flex>
        <HorizontalDivider />
        <Flex direction="column" gap={8}>
          <Flex justify="space-between">
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Combination',
                id: 'ApRTR3',
              })}
            </T>
            <CustomSwitch
              checked={relevantData.combiScreen}
              disabled={
                (disabled ||
                  (relevantData.splitScreen &&
                    relevantData.splitScreenChannels.channelOne === null &&
                    relevantData.splitScreenChannels.channelTwo === null)) ??
                false
              }
              onChange={() => {
                const combiScreen = !relevantData.combiScreen;

                // Determine if we need to disable splitscreen due to any any
                const shouldDisableSplit =
                  relevantData.splitScreen &&
                  relevantData.combiScreenChannels.channelOne === null &&
                  relevantData.combiScreenChannels.channelTwo === null;

                updateNodeData(selectedNode.id, {
                  ...selectedNode.data,
                  options: {
                    ...relevantData,
                    combiScreen,
                    // turn off combiScreen if necessary
                    splitScreen: shouldDisableSplit
                      ? false
                      : relevantData.splitScreen,
                  },
                });
              }}
            />
          </Flex>
          {relevantData.combiScreen && (
            <Flex direction="column" gap={8}>
              {renderChannelSelects(
                'combiScreenChannels',
                'Combination Channels',
              )}
            </Flex>
          )}
        </Flex>
        <HorizontalDivider />
      </StyledSettingsContent>
      <FAQSection faqs={faqs} />
    </StyledSettings>
  );
}

export default NFCSettingsAll;
